import { gql } from 'apollo-angular';
import { USER_FRAGMENT } from './fragments.graphql';
import { GraphQLInputTypes, GraphQLTypes } from './types.graphql';

export namespace GraphQLMutations {
  export const SIGN_UP_BY_EMAIL = gql<
    GraphQLTypes.Result<boolean>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.RegisterPlayerByEmailInput>
  >`
    mutation RegisterPlayerByEmail($registerPlayerByEmailInput: RegisterPlayerByEmailInput!) {
      result: site_registerPlayerByEmail(registerPlayerByEmailInput: $registerPlayerByEmailInput)
    }
  `;

  export const KYC_ACCESS_TOKEN = gql<GraphQLTypes.Result<GraphQLTypes.KycToken>, GraphQLInputTypes.Variables<any>>`
    mutation createKycAccessToken($kycRequestId: UUID!) {
      result: site_createKycAccessToken(kycRequestId: $kycRequestId) {
        token
      }
    }
  `;

  export const PAYMENT_METHOD_LIST = gql<GraphQLTypes.Result<any>, GraphQLInputTypes.PaymentMethodListInput>`
    mutation createPaymentSession($walletId: UUID!, $paymentTransactionType: SitePaymentTransactionType!) {
      result: site_createPaymentSession(walletId: $walletId, paymentTransactionType: $paymentTransactionType) {
        methods {
          alias
          methodType
          strategyType
          requiredFields {
            name
            inputType
            options {
              value
              label
            }
            defaultValue
            placeholder
            regExp
            min
            max
            step
          }
          paymentDetails {
            paymentOptionId
            paymentInfo
          }
          maxAmount
          minAmount
          predefinedAmount
          preselected
          step
          presetAmounts
          disabled
        }
        id
        notificationText
        kycType
        kycAmountLeft
      }
    }
  `;

  export const SIGN_IN_BY_EMAIL = gql<GraphQLTypes.Result<string>, GraphQLInputTypes.Variables<GraphQLInputTypes.LoginPlayerByEmailInput>>`
    mutation LoginPlayerByEmail($loginPlayerByEmailInput: LoginPlayerByEmailInput!) {
      result: site_loginPlayerByEmail(loginPlayerByEmailInput: $loginPlayerByEmailInput)
    }
  `;

  export const LOGOUT = gql<GraphQLTypes.Result<boolean>, any>`
    mutation Logout {
      result: site_logout
    }
  `;

  export const RESET_PASSWORD_BY_EMAIL = gql<
    GraphQLTypes.Result<boolean>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.ResetPlayerPasswordInput>
  >`
    mutation ResetPlayerPassword($resetPlayerPasswordInput: ResetPlayerPasswordInput!) {
      result: site_resetPlayerPassword(resetPlayerPasswordInput: $resetPlayerPasswordInput)
    }
  `;

  export const UPDATE_USER_PASSWORD_BY_TOKEN = gql<
    GraphQLTypes.Result<GraphQLTypes.PlayerAuth>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.UpdatePlayerPasswordByTokenInput>
  >`
    mutation UpdatePlayerPasswordByToken($updatePlayerPasswordByTokenInput: UpdatePlayerPasswordByTokenInput!) {
      result: site_updatePlayerPasswordByToken(updatePlayerPasswordByTokenInput: $updatePlayerPasswordByTokenInput) {
        loggedIn
        player {
          ...AllUserInfo
        }
      }
    }
    ${USER_FRAGMENT.ALL_USER_INFO}
  `;

  export const UPDATE_USER_PROFILE = gql<
    GraphQLTypes.Result<GraphQLTypes.SitePlayer>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.UpdatePlayerInput>
  >`
    mutation UpdatePlayerProfile($updatePlayerInput: UpdatePlayerInput!) {
      result: site_updatePlayer(updatePlayerInput: $updatePlayerInput) {
        ...AllUserInfo
      }
    }
    ${USER_FRAGMENT.ALL_USER_INFO}
  `;

  export const UPDATE_USER_ADDRESS = gql<
    GraphQLTypes.Result<GraphQLTypes.PlayerAddress>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.UpdatePlayerAddressInput>
  >`
    mutation UpdatePlayerAddress($updatePlayerAddressInput: UpdatePlayerAddressInput!) {
      result: site_updatePlayerAddress(updatePlayerAddressInput: $updatePlayerAddressInput) {
        ...UserAddress
      }
    }
    ${USER_FRAGMENT.USER_ADDRESS}
  `;

  export const UPDATE_USER_RECEIVE_PROMO = gql<
    GraphQLTypes.Result<GraphQLTypes.SitePlayer>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.UpdatePlayerInput>
  >`
    mutation UpdatePlayerProfile($updatePlayerInput: UpdatePlayerInput!) {
      result: site_updatePlayer(updatePlayerInput: $updatePlayerInput) {
        receivePromo
      }
    }
  `;

  export const UPDATE_USER_RECEIVE_SMS = gql<
    GraphQLTypes.Result<GraphQLTypes.SitePlayer>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.UpdatePlayerInput>
  >`
    mutation UpdatePlayerProfile($updatePlayerInput: UpdatePlayerInput!) {
      result: site_updatePlayer(updatePlayerInput: $updatePlayerInput) {
        receivePromo
      }
    }
  `;

  export const UPDATE_USER_RECEIVE_EMAIL = gql<
    GraphQLTypes.Result<GraphQLTypes.SitePlayer>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.UpdatePlayerInput>
  >`
    mutation UpdatePlayerProfile($updatePlayerInput: UpdatePlayerInput!) {
      result: site_updatePlayer(updatePlayerInput: $updatePlayerInput) {
        emailNotificationConsent
      }
    }
  `;

  export const UPDATE_USER_PHONE = gql<
    GraphQLTypes.Result<GraphQLTypes.SitePlayer>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.UpdatePlayerInput>
  >`
    mutation UpdatePlayerInput($updatePlayerInput: UpdatePlayerInput!) {
      result: site_updatePlayer(updatePlayerInput: $updatePlayerInput) {
        phone
      }
    }
  `;

  export const CREATE_WALLET = gql<
    GraphQLTypes.Result<GraphQLTypes.SiteWallet>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.CreateSiteWalletInput>
  >`
    mutation CreateWallet($createSiteWalletInput: CreateSiteWalletInput!) {
      result: site_createWallet(createSiteWalletInput: $createSiteWalletInput) {
        balance
        currency
        id
      }
    }
  `;

  export const SET_WALLET = gql<GraphQLTypes.Result<boolean>, GraphQLInputTypes.WalletInfoInput>`
    mutation SetWallet($walletId: UUID!) {
      result: site_setDefaultWallet(walletId: $walletId)
    }
  `;

  export const CREATE_DEPOSIT_SESSION = gql<GraphQLTypes.Result<any>, GraphQLInputTypes.Variables<any>>`
    mutation createDepositTransaction($CreateDepositTransactionInput: CreateDepositTransactionInput!) {
      result: site_createDepositTransaction(createDepositTransactionInput: $CreateDepositTransactionInput) {
        transactionId
        strategy
        strategyData {
          ... on SHPPSitePaymentSessionStrategyData {
            url
            qrCodeUrl
            rate
            address
          }
          ... on S2SSitePaymentSessionStrategyData {
            status
            url
            qrCodeUrl
            rate
            address
          }
        }
      }
    }
  `;

  export const CREATE_WITHDRAWAL_SESSION = gql<GraphQLTypes.Result<any>, GraphQLInputTypes.Variables<any>>`
    mutation createWithdrawalTransaction($CreateWithdrawalTransactionInput: CreateWithdrawalTransactionInput!) {
      result: site_createWithdrawalTransaction(createWithdrawalTransactionInput: $CreateWithdrawalTransactionInput) {
        transactionId
        strategy
        strategyData {
          ... on SHPPSitePaymentSessionStrategyData {
            url
            qrCodeUrl
            rate
            address
          }
          ... on S2SSitePaymentSessionStrategyData {
            status
            url
            qrCodeUrl
            rate
            address
          }
        }
      }
    }
  `;

  export const CANCEL_WITHDRAWAL = gql<
    GraphQLTypes.Result<GraphQLTypes.SitePaymentTransaction>,
    GraphQLInputTypes.CancelWithdrawalInfoInput
  >`
    mutation CancelWithdrawal($transactionId: UUID!) {
      result: site_cancelWithdrawal(transactionId: $transactionId) {
        currency
        amount
        createdAt
        id
        methodName
        paymentInfo
        providerName
        status
        type
      }
    }
  `;

  export const CONFIRM_EMAIL_BY_TOKEN = gql<GraphQLTypes.Result<boolean>, GraphQLInputTypes.TokenInput>`
    mutation SiteConfirmEmailByToken($token: UUID!) {
      result: site_confirmEmailByToken(token: $token)
    }
  `;

  export const CREATE_GAME_SESSION = gql<GraphQLTypes.Result<GraphQLTypes.GameSessionLauncher>, GraphQLInputTypes.GameSessionLauncherInput>`
    mutation CreateGameSession($gameId: UUID!, $walletId: UUID!) {
      site_createGameSession(gameId: $gameId, walletId: $walletId) {
        result: launchURL
      }
    }
  `;

  export const CREATE_DEMO_GAME_SESSION = gql<
    GraphQLTypes.Result<GraphQLTypes.GameSessionLauncher>,
    GraphQLInputTypes.GameSessionLauncherInput
  >`
    mutation CreateDemoGameSession($gameId: UUID!) {
      site_createDemoGameSession(gameId: $gameId) {
        result: launchURL
      }
    }
  `;

  export const UPDATE_BONUS_STATUS = gql<GraphQLTypes.Result<boolean>, GraphQLInputTypes.UpdateBonusStatusInput>`
    mutation UpdateBonusStatus($walletBonusBalanceId: UUID!, $bonusStatus: SiteUpdateBonusStatus!) {
      result: site_updateBonusStatus(walletBonusBalanceId: $walletBonusBalanceId, bonusStatus: $bonusStatus)
    }
  `;

  export const UPDATE_USER_LANG = gql<
    GraphQLTypes.Result<GraphQLTypes.SitePlayer>,
    GraphQLInputTypes.Variables<GraphQLInputTypes.UpdatePlayerInput>
  >`
    mutation UpdatePlayerLang($updatePlayerInput: UpdatePlayerInput!) {
      result: site_updatePlayer(updatePlayerInput: $updatePlayerInput) {
        language
      }
    }
  `;

  export const ADD_USER_SEGMENT = gql<
    GraphQLTypes.Result<Boolean>,
    GraphQLInputTypes.UpdateUserSegmentInput
  >`
    mutation AddUserSegment($segmentId: UUID!) {
      result: site_addSegment(segmentId: $segmentId)
    }
  `;

  export const DELETE_USER_SEGMENT = gql<
    GraphQLTypes.Result<Boolean>,
    GraphQLInputTypes.UpdateUserSegmentInput
  >`
    mutation DeleteUserSegment($segmentId: UUID!) {
      result: site_deleteSegment(segmentId: $segmentId)
    }
  `;

  export const OPEN_LOOTBOX = gql<GraphQLTypes.Result<GraphQLTypes.SiteLootboxOpenResponse>, GraphQLInputTypes.LootboxOpenInput>`
    mutation OpenLootbox($siteLootboxOpenId: UUID!, $walletId: UUID!) {
      result: site_lootboxOpen(id: $siteLootboxOpenId, walletId: $walletId) {
        success
        data {
          bonus {
            ... on SiteLootboxItemBonus {
              id
              settings {
                ... on SiteCashBonusTemplateSettings {
                  amountSettings {
                    currency
                    amount
                  }
                }
                ... on SiteFreespinBonusTemplateSettings {
                  betNumber
                  gameId
                }
              }
            }
            ... on SiteLootboxItemBonusEmpty {
              type
            }
          }
        }
      }
    }
`;

export const CANCEL_LOOTBOX = gql<GraphQLTypes.Result<boolean>, GraphQLInputTypes.LootboxCancelInput>`
  mutation CancelLootbox($siteLootboxCancelId: UUID!) {
    result: site_lootboxCancel(id: $siteLootboxCancelId)
  }
`;

export const REQUEST_OTP = gql<GraphQLTypes.Result<GraphQLTypes.OTPVerificationStatusType>, GraphQLInputTypes.Variables<any>>`
  mutation RequestOTP {
    result: site_requestOTP {
      status
    }
  }
`;

export const VERIFY_OTP = gql<GraphQLTypes.Result<GraphQLTypes.OTPVerificationStatusType>, GraphQLInputTypes.VerifyOTPInput>`
  mutation VerifyOTP($verifyOtpInput: VerifyOTPInput!) {
    result: site_verifyOTP(verifyOTPInput: $verifyOtpInput) {
      status
    }
  }
`;

}
